import { Signal, Type } from '@angular/core';
import { SIGNAL } from '@angular/core/primitives/signals';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { isEmpty } from '@kinet-open-network/shared/util';
import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';
import {
  FormlyAttributeEvent,
  FormlyHookConfig,
  ValidationMessageOption,
} from '@ngx-formly/core/lib/models';
import { Observable } from 'rxjs';
import { ButtonWrapperProps } from '../wrappers/button/filed-button-wrapper.component';

type FieldExpression =
  | string
  | ((field: FormlyFieldConfig) => any)
  | Observable<any>;

interface FormlyValidation<T, R> {
  validation?: (string | T)[] | any;
  [key: string]: FormlyValidatorFn<R> | FormlyValidatorExpressionFn<R> | any;
}
declare type FormlyValidatorFn<T> = (
  control: AbstractControl,
  field: FormlyFieldConfig,
) => T;
interface FormlyValidatorExpressionFn<T> {
  expression: FormlyValidatorFn<T>;
  message:
    | string
    | ((error: any, field: FormlyFieldConfig) => string | Observable<string>);
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type FormlyPartailConfig<T> = T extends Function
  ? T
  : T extends object
    ? { [P in keyof T]?: FormlyPartailConfig<T[P]> }
    : T;

export type InferProps<T> =
  T extends FormlyFieldConfig<infer Props> ? Partial<Props> : never;

interface DefaultProps {
  className?: string;
}

export class FieldBuilderBase<
  FieldConfig extends FormlyFieldConfig & {
    autoClear?: boolean;
  } = FormlyFieldConfig,
> {
  protected config: FormlyPartailConfig<FieldConfig>;
  private defaultProps: DefaultProps = {};

  constructor(
    object?: FormlyPartailConfig<FieldConfig>,
    defaultProps: DefaultProps = {},
  ) {
    this.config = { ...object } as FormlyPartailConfig<FieldConfig>;
    this.defaultProps = defaultProps;
    this.setDefaultProps();
  }

  private setDefaultProps() {
    this.config.className = this.defaultProps.className;
  }

  private buttonProps?: ButtonWrapperProps;

  getProps(): InferProps<FieldConfig> {
    return this.config.props as InferProps<FieldConfig>;
  }

  key(key: string) {
    this.config.key = key;
    return this;
  }

  build(): FieldConfig {
    if (this.buttonProps) {
      this.props(this.buttonProps as InferProps<FieldConfig>);
      if (!this.config.wrappers) {
        this.config.wrappers = ['form-field', 'field-button'];
      } else if (!this.config.wrappers.includes('field-button')) {
        this.config.wrappers = [
          ...(this.config.wrappers || []),
          'field-button',
        ];
      }
    }
    return this.config as FieldConfig;
  }

  required<P>(valueOrCondition: boolean | FieldExpression | Signal<P> = true) {
    return this.propOrExpression('props.required', valueOrCondition);
  }

  disabled<P>(valueOrCondition: boolean | FieldExpression | Signal<P> = true) {
    return this.propOrExpression('props.disabled', valueOrCondition);
  }

  placeholder<P>(valueOrCondition: string | FieldExpression | Signal<P> = '') {
    return this.propOrExpression('props.placeholder', valueOrCondition);
  }

  disabledReason<P>(
    valueOrCondition: string | FieldExpression | Signal<P> = '',
  ) {
    return this.propOrExpression('props.disabledReason', valueOrCondition);
  }

  readonly<P>(valueOrCondition: boolean | FieldExpression | Signal<P> = true) {
    return this.propOrExpression('props.readonly', valueOrCondition);
  }

  hide<P>(
    valueOrCondition: boolean | FieldExpression | Signal<P> = true,
    resetOnHide?: boolean,
  ) {
    if (resetOnHide !== undefined) {
      this.config.resetOnHide = resetOnHide;
    }
    return this.propOrExpression('hide', valueOrCondition);
  }

  defaultValue(value: any): this {
    this.config.defaultValue = value;
    return this;
  }

  props(
    value:
      | (InferProps<FieldConfig> & {
          [additionalProperties: string]: any;
        })
      | never,
  ): this {
    this.config.props = { ...this.config.props, ...value };
    return this;
  }

  propOrExpression<T = any>(
    key: string,
    valueOrExpression: FieldExpression | Signal<T> | any,
  ) {
    const expressionRegexp = /^(([\S]*\.[\S]*)|(.*[!=]==?.*))$/g;
    const isSignal = valueOrExpression && valueOrExpression[SIGNAL];
    if (isSignal) {
      valueOrExpression = toObservable(valueOrExpression);
    }
    if (
      typeof valueOrExpression === 'function' ||
      valueOrExpression instanceof Observable ||
      (typeof valueOrExpression === 'string' &&
        valueOrExpression.match(expressionRegexp))
    ) {
      return this.expressions({
        ...this.config.expressions,
        [key]: valueOrExpression,
      });
    } else if (key.startsWith('props.')) {
      return this.props({
        [key.replace('props.', '')]: valueOrExpression,
      } as InferProps<FieldConfig>);
    } else {
      this.config[key as keyof FormlyPartailConfig<FieldConfig>] =
        valueOrExpression;
      return this;
    }
  }

  modelOptions(value: { updateOn?: 'change' | 'blur' | 'submit' }) {
    this.config.modelOptions = value;
    return this;
  }

  validation(value: {
    messages?: {
      [messageProperties: string]: ValidationMessageOption['message'];
    };
    show?: boolean;
    [additionalProperties: string]: any;
  }) {
    this.config.validation = value;
    return this;
  }

  validators(value: FormlyValidation<ValidatorFn, boolean>) {
    this.config.validators = value;
    return this;
  }

  asyncValidators(
    value: FormlyValidation<
      AsyncValidatorFn,
      Promise<boolean> | Observable<boolean>
    >,
  ) {
    this.config.asyncValidators = value;
    return this;
  }

  parsers(parsers: ((value: any) => any)[]) {
    this.config.parsers = [
      ...(Array.isArray(this.config.parsers)
        ? this.config.parsers
        : [this.config.parsers]),
      ...parsers,
    ].filter(Boolean);
    return this;
  }

  ifEmpty(value: any) {
    return this.parsers([(val) => (isEmpty(val) ? value : val)]);
  }

  nullIfEmpty() {
    return this.ifEmpty(null);
  }

  template(value: string) {
    this.config.template = value;
    return this;
  }

  wrappers(value: (string | Type<FieldWrapper>)[]) {
    this.config.wrappers = value;
    return this;
  }

  description(value: string | Signal<string> | FieldExpression) {
    return this.propOrExpression('props.description', value);
  }

  tooltip(value: string | Signal<string> | FieldExpression) {
    return this.propOrExpression('props.tooltip', value);
  }

  expressions(value: {
    [property: string]:
      | string
      | ((field: FormlyFieldConfig) => any)
      | Observable<any>;
  }) {
    this.config.expressions = { ...this.config.expressions, ...value };
    return this;
  }

  className(value: string) {
    this.config.className = [this.defaultProps.className, value]
      .filter(Boolean)
      .join(' ');
    return this;
  }

  fieldGroupClassName(value: string) {
    this.config.fieldGroupClassName = value;
    return this;
  }

  hooks(value: FormlyHookConfig) {
    this.config.hooks = value;
    return this;
  }

  fieldGroup(
    value: FieldBuilderBase[],
    options?: { fieldWrappers?: string[] },
  ) {
    this.config.fieldGroup = value?.map((builder) => {
      if (options?.fieldWrappers) {
        builder.wrappers(options.fieldWrappers);
      }
      return builder.build();
    });
    return this;
  }

  button(props: ButtonWrapperProps) {
    this.buttonProps = props;
    return this;
  }

  options(value: any[] | Observable<any[]>) {
    return this.props({ options: value } as InferProps<FieldConfig>);
  }

  focus(value: FormlyAttributeEvent) {
    return this.props({ focus: value } as InferProps<FieldConfig>);
  }

  blur(value: FormlyAttributeEvent) {
    return this.props({ blur: value } as InferProps<FieldConfig>);
  }

  keyup(value: FormlyAttributeEvent) {
    return this.props({ keyup: value } as InferProps<FieldConfig>);
  }

  keydown(value: FormlyAttributeEvent) {
    return this.props({ keydown: value } as InferProps<FieldConfig>);
  }

  click(value: FormlyAttributeEvent) {
    return this.props({ click: value } as InferProps<FieldConfig>);
  }

  change(value: FormlyAttributeEvent) {
    return this.props({ change: value } as InferProps<FieldConfig>);
  }

  keypress(value: FormlyAttributeEvent) {
    return this.props({ keypress: value } as InferProps<FieldConfig>);
  }
}

export class FieldBuilderGroup extends FieldBuilderBase {
  array(field: FieldBuilderBase) {
    this.config.fieldArray = field.build();
    return this;
  }
}

export class FieldBuilderArray<
  FieldConfig extends FormlyFieldConfig = FormlyFieldConfig,
> extends FieldBuilderBase<FieldConfig> {
  constructor(object?: FormlyPartailConfig<FieldConfig>) {
    super(object);
  }

  array(field: FieldBuilderBase) {
    this.config.fieldArray = field.build();
    return this;
  }
}
